import { ApiError } from '../../services/api';
import type { OfferResponse } from '../../types/offers';
import { REJECTION_REASONS } from '../../types/offers';
import type {
  AdminPackageRow,
  PackageHistoryAction,
  PackageResponse,
  PackageStatus,
} from '../../types/packages';
import { formatAed, formatDay, formatInstant } from '../offers/offerReviewModel';

/** Spec 081b — labels, formatting and error mapping for the admin package screens (pattern `offerReviewModel.ts`). */

export const PACKAGE_STATE_CHANGED_MESSAGE = 'This package changed, review again';

export const PACKAGE_STATUS_LABELS: Record<PackageStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending approval',
  SCHEDULED: 'Scheduled',
  LIVE: 'Live',
  PAUSED: 'Paused',
  ENDED: 'Ended',
  REJECTED: 'Rejected',
  SUSPENDED: 'Suspended',
};

/** `admin-badge` modifier per status (base colours in admin.css, extras in orders.css). */
export const PACKAGE_STATUS_BADGE: Record<PackageStatus, string> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'pending',
  SCHEDULED: 'order-info',
  LIVE: 'approved',
  PAUSED: 'warning',
  ENDED: 'neutral',
  REJECTED: 'rejected',
  SUSPENDED: 'rejected',
};

/** `garage_packages.unavailable_reason` → what the admin reads. Unknown codes are humanised. */
export const UNAVAILABLE_REASON_LABELS: Record<string, string> = {
  ITEM_UNAVAILABLE: 'A service in the package is unavailable',
  ITEM_INACTIVE: 'A service in the package is inactive',
  ITEM_BLOCKED: 'A service in the package is blocked',
  ITEM_DELETED: 'A service in the package was deleted',
  VALUE_HIDDEN: 'A fixed choice is no longer offered',
  NO_BOOKABLE_VARIANT: 'A service has no bookable price',
  PRICE_DRIFT: 'The package price is no longer below the total',
  NOT_AVAILABLE_FOR_VEHICLE: 'Not available for any car',
  FEATURE_DISABLED: 'Packages are switched off',
  NOT_LIVE: 'Not live',
};

export const HISTORY_ACTION_LABELS: Record<PackageHistoryAction, string> = {
  PACKAGE_APPROVED: 'Approved',
  PACKAGE_REJECTED: 'Rejected',
  PACKAGE_SUSPENDED: 'Suspended',
  PACKAGE_ENDED: 'Ended',
  PACKAGE_ADMIN_EDITED: 'Edited by admin',
};

export const HISTORY_ACTION_BADGE: Record<PackageHistoryAction, string> = {
  PACKAGE_APPROVED: 'approved',
  PACKAGE_REJECTED: 'rejected',
  PACKAGE_SUSPENDED: 'rejected',
  PACKAGE_ENDED: 'neutral',
  PACKAGE_ADMIN_EDITED: 'update-requested',
};

const humanise = (code: string) => code.toLowerCase().replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export function unavailableReasonLabel(code: string | null | undefined): string {
  if (!code) return '';
  return UNAVAILABLE_REASON_LABELS[code] ?? humanise(code);
}

export function rejectionReasonLabel(code: string | null | undefined): string {
  if (!code) return '';
  return REJECTION_REASONS.find((r) => r.code === code)?.label ?? humanise(code);
}

type Priced = Pick<AdminPackageRow, 'pricingMode' | 'packagePrice' | 'discountPercent' | 'savingPercent'>;

export function modeLabel(p: Pick<AdminPackageRow, 'pricingMode'>): string {
  return p.pricingMode === 'FIXED' ? 'Fixed price' : 'Percent off';
}

/** "AED 150" for FIXED, "20% off" for PERCENT. */
export function priceText(p: Priced): string {
  if (p.pricingMode === 'FIXED') return formatAed(p.packagePrice);
  return p.discountPercent == null ? '—' : `${p.discountPercent}% off`;
}

/** The DecisionBar badge text: "AED 150 · save 25%" / "20% OFF". */
export function decisionLabel(p: Priced): string {
  if (p.pricingMode === 'PERCENT') return p.discountPercent == null ? '—' : `${p.discountPercent}% OFF`;
  const save = p.savingPercent != null ? ` · save ${p.savingPercent}%` : '';
  return `${formatAed(p.packagePrice)}${save}`;
}

export function formatPackageWindow(startsAt: string | null, endsAt: string | null): string {
  if (!startsAt && !endsAt) return 'Always on';
  if (!endsAt) return `From ${formatDay(startsAt)}`;
  if (!startsAt) return `Until ${formatDay(endsAt)}`;
  return `${formatDay(startsAt)} – ${formatDay(endsAt)}`;
}

/** The garage tab's "approval state" column. */
export function approvalState(
  row: Pick<AdminPackageRow, 'status' | 'submittedAt' | 'approvedAt' | 'rejectionReasonCode'>,
): string {
  switch (row.status) {
    case 'DRAFT': return 'Not submitted';
    case 'PENDING_APPROVAL': return `Waiting since ${formatInstant(row.submittedAt)}`;
    case 'REJECTED': return `Rejected${row.rejectionReasonCode ? `: ${rejectionReasonLabel(row.rejectionReasonCode)}` : ''}`;
    case 'SUSPENDED': return 'Suspended by Garagat';
    default: return row.approvedAt ? `Approved ${formatInstant(row.approvedAt)}` : 'Approved';
  }
}

/**
 * The shared `DecisionBar` takes an `OfferResponse`. It reads `status`, `revisionOf`, `titleEn` and the
 * label, so a package is mapped onto that shape (statuses are a subset of the offer statuses).
 */
export function toDecisionOffer(p: PackageResponse): OfferResponse {
  return {
    id: p.id, garageId: p.garageId, status: p.status, revisionOf: null,
    titleEn: p.nameEn, titleAr: p.nameAr, subtitleEn: null, subtitleAr: null,
    startsAt: p.startsAt ?? '', endsAt: p.endsAt ?? '',
    discountType: p.pricingMode === 'PERCENT' ? 'PERCENT' : 'FIXED',
    discountValue: (p.pricingMode === 'PERCENT' ? p.discountPercent : p.savingAmount) ?? 0,
    maxDiscountPerBooking: null, label: decisionLabel(p), maxBookings: null, bookingsUsed: null,
    items: [], banner: null, bannerAr: null,
    rejectionReasonCode: p.rejectionReasonCode, rejectionReason: p.rejectionReason, suspendReason: p.suspendReason,
    hiddenReason: null, submittedAt: p.submittedAt, version: p.version, createdAt: p.createdAt, updatedAt: p.updatedAt,
  };
}

export interface PackageDecisionError {
  message: string;
  /** True when the page must reload the package before another decision (stale version / status). */
  reload: boolean;
}

const PAST = { approve: 'approved', reject: 'rejected', suspend: 'suspended', load: 'loaded' } as const;

/** Maps the design §3 package error codes to what the admin reads. */
export function packageDecisionError(err: unknown, action: keyof typeof PAST): PackageDecisionError {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'PACKAGE_STATE_CHANGED':
        return { message: PACKAGE_STATE_CHANGED_MESSAGE, reload: true };
      case 'PACKAGE_INVALID_TRANSITION':
        return { message: err.serverMessage || `This package can no longer be ${PAST[action]}.`, reload: true };
      case 'PACKAGE_NOT_FOUND':
        return { message: 'This package no longer exists.', reload: false };
      case 'REASON_TOO_SHORT':
        return { message: 'Give the partner a reason (at least 10 characters)', reload: false };
      default:
        break;
    }
    if (err.status === 403) return { message: 'You do not have access to review packages.', reload: false };
    if (err.status === 404) return { message: 'This package no longer exists.', reload: false };
  }
  return { message: err instanceof Error && err.message ? err.message : `The package could not be ${PAST[action]}.`, reload: false };
}
